import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Gift } from "lucide-react";
import { getErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSiteConfig } from "../context/SiteConfigContext";
import { NeonButton, NeonCard } from "../components/Neon";

const Field = ({ label, testid, ...props }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-[var(--text-muted)]">{label}</label>
    <input data-testid={testid} className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-xl px-3 py-2.5 text-white focus:outline-none focus:border-[var(--neon-cyan)]" {...props} />
  </div>
);

export default function Register() {
  const { register } = useAuth();
  const { maintenanceMode } = useSiteConfig();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  // A referral code may arrive via ?ref=CODE on this page or any earlier page (persisted by RouteTracker).
  const [referralCode, setReferralCode] = useState("");
  useEffect(() => {
    const fromUrl = sp.get("ref");
    if (fromUrl) localStorage.setItem("rrc_ref", fromUrl.trim());
    setReferralCode((fromUrl || localStorage.getItem("rrc_ref") || "").trim());
  }, [sp]);
  const [f, setF] = useState({ full_name: "", email: "", password: "", mobile_phone: "", date_of_birth: "", residential_address: "", neuro_features: false });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (maintenanceMode) { toast.error("New sign-ups are paused while the site is under maintenance. Please check back soon."); return; }
    setLoading(true);
    try {
      await register({ ...f, referral_code: referralCode || undefined });
      localStorage.removeItem("rrc_ref");
      toast.success("Account created! Verify your email & phone to start entering.");
      navigate("/profile");
    } catch (err) {
      toast.error(getErr(err, "Registration failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-lg px-4 py-12">
      <NeonCard variant="flow" innerClassName="p-8">
        <h1 className="font-display uppercase text-4xl text-white glow-white">Create Account</h1>
        <p className="mt-1 text-sm text-[var(--text-muted)]">18+ only. You'll verify email &amp; phone next.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          {referralCode && (
            <div data-testid="register-referral-note" className="flex items-center gap-2 rounded-xl border border-[rgba(255,210,74,.5)] bg-[rgba(255,210,74,.08)] px-4 py-3 text-sm text-[var(--neon-gold)]">
              <Gift className="h-4 w-4 shrink-0" />
              <span>Referral applied: <span className="font-mono text-white">{referralCode}</span> — you &amp; your friend may both earn rewards.</span>
            </div>
          )}
          {maintenanceMode && (
            <div data-testid="register-maintenance-note" className="rounded-xl border border-[rgba(255,184,0,.5)] bg-[rgba(255,184,0,.08)] px-4 py-3 text-sm text-[var(--neon-gold)]">
              New sign-ups are paused while the site is under maintenance. Please check back soon.
            </div>
          )}
          <Field label="Full name" testid="register-full-name" required value={f.full_name} onChange={set("full_name")} />
          <Field label="Email" testid="register-email" type="email" autoComplete="username" spellCheck="false" required value={f.email} onChange={set("email")} />
          <Field label="Password" testid="register-password" type="password" autoComplete="new-password" required value={f.password} onChange={set("password")} />
          <Field label="Mobile phone" testid="register-phone" required placeholder="+44..." value={f.mobile_phone} onChange={set("mobile_phone")} />
          <Field label="Date of birth" testid="register-dob" type="date" required value={f.date_of_birth} onChange={set("date_of_birth")} />
          <Field label="Residential address" testid="register-address" required value={f.residential_address} onChange={set("residential_address")} />
          <label data-testid="register-neuro-toggle" className="flex items-start gap-3 rounded-xl border border-[rgba(0,229,255,.3)] bg-[rgba(0,229,255,.05)] px-4 py-3 cursor-pointer hover:border-[rgba(0,229,255,.5)] transition-colors">
            <input type="checkbox" checked={f.neuro_features} onChange={(e) => setF((s) => ({ ...s, neuro_features: e.target.checked }))} className="mt-0.5 h-4 w-4 accent-[var(--neon-cyan)]" />
            <span className="text-sm">
              <span className="block font-display uppercase tracking-wide text-[var(--neon-cyan)]">Enable Quick Buy &amp; simplified mode</span>
              <span className="block mt-0.5 text-xs text-[var(--text-muted)]">Neurodivergent-friendly: fewer steps, clearer focus and one-tap entry. You can change this anytime in your profile.</span>
            </span>
          </label>
          <NeonButton type="submit" variant="pink" className="w-full" data-testid="register-submit" loading={loading} disabled={maintenanceMode}>{maintenanceMode ? "Sign-ups Paused" : "Create Account"}</NeonButton>
        </form>
        <p className="mt-5 text-sm text-[var(--text-muted)]">Already a member? <Link to="/login" className="text-[var(--neon-pink)] hover:underline">Log in</Link></p>
      </NeonCard>
    </div>
  );
}
